import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import moment from 'moment';
import { Modal, ModalHeader, ModalBody } from 'reactstrap';

import ActionButton from 'atoms/ActionButton';
import SelectAutoComplete from 'atoms/SelectAutoComplete';
import Table from 'atoms/Table';
import { headerStyle } from 'containers/App/constants';
import approveImage from 'svgs/approve';
import hideImage from 'svgs/hide';
import addImage from 'svgs/add';
import ConfirmationModal from 'components/ConfirmationModal';
import api from 'utils/api';

import { makeSelectAllClients } from 'containers/CliniciansAndClients/selectors';
import { loadAllClients } from 'containers/CliniciansAndClients/actions';
import { fetchReferrers, approveReferrer, deactivateReferrer } from './actions';
import {
	makeSelectReferrers,
	makeSelectPagination,
	makeSelectLoading,
	makeSelectError,
	makeSelectActionLoading,
	makeSelectActionError,
} from './selectors';

const getStatusLabel = (referrer) => {
	if (!referrer.user?.isVerified) return 'Pending Approval';
	if (!referrer.isActive) return 'Inactive';
	return 'Approved';
};

const getStatusBadgeClass = (referrer) => {
	if (!referrer.user?.isVerified) return 'badge-warning';
	if (!referrer.isActive) return 'badge-secondary';
	return 'badge-success';
};

const AdminReferrers = (props) => {
	const {
		referrers,
		loading,
		error,
		actionLoading,
		actionError,
		doFetchReferrers,
		doApproveReferrer,
		doDeactivateReferrer,
		doLoadAllClients,
		allClients,
	} = props;

	const [showLinkPatientModal, setShowLinkPatientModal] = useState(false);
	const [linkReferrerId, setLinkReferrerId] = useState(null);
	const [linkReferrerName, setLinkReferrerName] = useState('');
	const [selectedPatient, setSelectedPatient] = useState(null);
	const [signedAt, setSignedAt] = useState('');
	const [linkSubmitting, setLinkSubmitting] = useState(false);
	const [linkError, setLinkError] = useState('');
	const [confirmModalOpen, setConfirmModalOpen] = useState(false);
	const [confirmAction, setConfirmAction] = useState(null); // { type: 'approve'|'deactivate', id, name }

	useEffect(() => {
		doFetchReferrers();
		// Link Patient modal needs the patient roster from Redux. Other tabs
		// populate it (CliniciansAndClients, Measures) but admins may land
		// here directly without visiting those first.
		doLoadAllClients();
	}, []);

	const handleApprove = (id, name) => {
		setConfirmAction({ type: 'approve', id, name });
		setConfirmModalOpen(true);
	};

	const handleDeactivate = (id, name) => {
		setConfirmAction({ type: 'deactivate', id, name });
		setConfirmModalOpen(true);
	};

	const handleConfirm = () => {
		if (!confirmAction) return;
		if (confirmAction.type === 'approve') {
			doApproveReferrer(confirmAction.id);
		} else if (confirmAction.type === 'deactivate') {
			doDeactivateReferrer(confirmAction.id);
		}
	};

	// Close the confirmation modal once the async action finishes
	// (actionLoading transitions from true → false)
	const prevActionLoadingRef = React.useRef(actionLoading);
	useEffect(() => {
		if (prevActionLoadingRef.current === true && actionLoading === false) {
			setConfirmModalOpen(false);
			setConfirmAction(null);
		}
		prevActionLoadingRef.current = actionLoading;
	}, [actionLoading]);

	const handleCancelConfirm = () => {
		setConfirmModalOpen(false);
		setConfirmAction(null);
	};

	const resetLinkState = () => {
		setLinkReferrerId(null);
		setLinkReferrerName('');
		setSelectedPatient(null);
		setSignedAt('');
		setLinkError('');
	};

	const patientOptions = (allClients || []).map((p) => ({
		value: p.client?.patientId,
		label: `${p.client?.name || ''}${p.client?.dob ? ` (DOB: ${moment(p.client.dob).format('MM/DD/YYYY')})` : ''}`,
	}));

	const handleLinkPatientSubmit = async () => {
		if (!selectedPatient || !linkReferrerId || !signedAt) return;
		setLinkSubmitting(true);
		setLinkError('');
		try {
			await api.post('/admin/rois/create-manual', {
				patientId: selectedPatient.value,
				referrerId: linkReferrerId,
				clinicianName: linkReferrerName,
				signedAt,
			});
			setShowLinkPatientModal(false);
			resetLinkState();
			doFetchReferrers();
		} catch (err) {
			setLinkError(
				err.response?.data?.error?.message || err.response?.data?.error || 'Failed to create referrer link',
			);
		}
		setLinkSubmitting(false);
	};

	const columns = [
		{
			field: 'user',
			title: 'NAME',
			headerStyle,
			render: (row) => (
				<div className="mt-2 font-13">{row.user?.name}</div>
			),
		},
		{
			field: 'email',
			title: 'EMAIL',
			headerStyle,
			render: (row) => (
				<div className="mt-2 font-13">{row.user?.email}</div>
			),
		},
		{
			field: 'practiceName',
			title: 'PRACTICE',
			headerStyle,
			render: (row) => (
				<div className="mt-2 font-13">{row.practiceName}</div>
			),
		},
		{
			field: 'status',
			title: 'STATUS',
			headerStyle,
			render: (row) => (
				<div className="mt-2">
					<span className={`badge ${getStatusBadgeClass(row)}`}>
						{getStatusLabel(row)}
					</span>
				</div>
			),
		},
		{
			field: 'createdAt',
			title: 'CREATED',
			headerStyle,
			render: (row) => (
				<div className="mt-2 font-13">
					{row.createdAt
						? moment(row.createdAt).format('MM/DD/YYYY')
						: ''}
				</div>
			),
		},
		{
			field: 'actions',
			title: 'ACTIONS',
			headerStyle: { ...headerStyle, textAlign: 'right' },
			render: (row) => (
				<div className="row justify-content-end mr-2">
					{!row.user?.isVerified && (
						<div className="col-auto px-0">
							<ActionButton
								image={approveImage}
								onClick={() =>
									!actionLoading &&
									handleApprove(row.id, row.user?.name)
								}
								hoverColor={actionLoading ? 'var(--main-light-grey)' : 'var(--main-color)'}
								fillColor={actionLoading ? 'var(--main-light-grey)' : 'var(--main-grey)'}
								alt={actionLoading ? 'Loading...' : 'Approve'}
								buttonStyle={actionLoading ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
							/>
						</div>
					)}
					{row.user?.isVerified && row.isActive && (
						<div className="col-auto px-0">
							<ActionButton
								image={addImage}
								hoverColor={actionLoading ? 'var(--main-light-grey)' : 'var(--main-color)'}
								fillColor={actionLoading ? 'var(--main-light-grey)' : 'var(--main-grey)'}
								onClick={() => {
									setLinkReferrerId(row.id);
									setLinkReferrerName(row.user?.name || '');
									setShowLinkPatientModal(true);
								}}
								alt="Link Patient"
							/>
						</div>
					)}
					{row.user?.isVerified && row.isActive && (
						<div className="col-auto px-0">
							<ActionButton
								image={hideImage}
								hoverColor={actionLoading ? 'var(--main-light-grey)' : 'var(--main-alert)'}
								fillColor={actionLoading ? 'var(--main-light-grey)' : 'var(--main-grey)'}
								onClick={() =>
									!actionLoading &&
									handleDeactivate(row.id, row.user?.name)
								}
								alt={actionLoading ? 'Loading...' : 'Deactivate'}
								buttonStyle={actionLoading ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
							/>
						</div>
					)}
				</div>
			),
			sorting: false,
		},
	];

	const rowStyle = (rowData) => {
		if (!rowData.isActive) {
			return { backgroundColor: 'rgb(255 231 231)' };
		}
		return {};
	};

	return (
		<div style={{ overflowX: 'hidden' }}>
			{actionError && (
				<div className="alert alert-danger" role="alert">
					{actionError}
				</div>
			)}
			<Table
				keyField="adminReferrers"
				data={referrers || []}
				columns={columns}
				placeholder="No referrers found"
				loading={loading}
				error={error}
				rowStyle={rowStyle}
			/>
			{showLinkPatientModal && (
				<Modal
					className="modal-dialog-centered"
					isOpen
					toggle={() => {
						setShowLinkPatientModal(false);
						resetLinkState();
					}}
				>
					<ModalHeader
						toggle={() => {
							setShowLinkPatientModal(false);
							resetLinkState();
						}}
					>
						Link Patient to {linkReferrerName}
					</ModalHeader>
					<ModalBody>
						<div className="mb-3">
							<SelectAutoComplete
								label="Patient"
								handleChange={(selected) => setSelectedPatient(selected)}
								options={patientOptions}
								value={selectedPatient}
								placeholder="Search by patient name..."
							/>
						</div>

						<div className="mb-3">
							<label className="form-font mb-2">Date Signed</label>
							<input
								type="date"
								className="form-control font-13"
								value={signedAt}
								onChange={(e) => setSignedAt(e.target.value)}
							/>
						</div>

						{linkError && (
							<div className="d-flex justify-content-center error-font mb-2">
								{linkError}
							</div>
						)}
						<div className="mt-4 mb-2">
							<button
								className="btn btn-primary btn-lg btn-block btn-color text-uppercase font-13"
								disabled={
									!selectedPatient || !signedAt || linkSubmitting
								}
								onClick={handleLinkPatientSubmit}
							>
								{linkSubmitting ? 'CREATING...' : 'CREATE REFERRER LINK'}
							</button>
						</div>
					</ModalBody>
				</Modal>
			)}
			{confirmModalOpen && confirmAction && (
				<ConfirmationModal
					isOpen={confirmModalOpen}
					title={
						confirmAction.type === 'approve'
							? 'Approve Referrer'
							: 'Deactivate Referrer'
					}
					message={
						confirmAction.type === 'approve'
							? `Are you sure you want to approve ${confirmAction.name}?`
							: `Are you sure you want to deactivate ${confirmAction.name}?`
					}
					confirmText={confirmAction.type === 'approve' ? 'Approve' : 'Deactivate'}
					cancelText="Cancel"
					onConfirm={handleConfirm}
					onCancel={handleCancelConfirm}
					loading={actionLoading}
					variant={confirmAction.type === 'deactivate' ? 'danger' : 'default'}
				/>
			)}
		</div>
	);
};

const mapStateToProps = createStructuredSelector({
	referrers: makeSelectReferrers(),
	pagination: makeSelectPagination(),
	loading: makeSelectLoading(),
	error: makeSelectError(),
	actionLoading: makeSelectActionLoading(),
	actionError: makeSelectActionError(),
	allClients: makeSelectAllClients(),
});

function mapDispatchToProps(dispatch) {
	return {
		dispatch,
		doFetchReferrers: (params) => dispatch(fetchReferrers(params)),
		doApproveReferrer: (id) => dispatch(approveReferrer(id)),
		doDeactivateReferrer: (id) => dispatch(deactivateReferrer(id)),
		doLoadAllClients: () => dispatch(loadAllClients()),
	};
}

export default connect(mapStateToProps, mapDispatchToProps)(AdminReferrers);
